import { Injectable } from '@angular/core';
import { CONSTANTS, TIME_SEPARATOR } from '../constants/constants';
import { TimeUnits } from '../types/misc';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap/datepicker/ngb-date-struct';
import { format, parse } from 'date-fns';
import { TranslateService } from './translate.service';

@Injectable()
export class DateTimeService {
  static formatTime(timeString: string): string {
    let { hours, minutes } = DateTimeService.parseTime(timeString);

    if (parseInt(hours) > 23) {
      hours = '23';
      minutes = '59';
    }

    return [hours, TIME_SEPARATOR, minutes].join('');
  }

  static parseTime(timeString: string): TimeUnits {
    timeString = timeString.replace(
      TranslateService.translate('minutes_unit_short'),
      TranslateService.translate('minutes_unit_extra_short')
    );

    const unitH = TranslateService.translate('hours_unit_short');
    const unitMin = TranslateService.translate('minutes_unit_extra_short');
    const unitSec = TranslateService.translate('seconds_unit_short');

    timeString = timeString.replace(/,/g, '.').replace(/\s/g, '');

    let hoursStr = '',
      minutesStr = '',
      secondsStr = '';

    if (
      timeString.indexOf(unitH) > -1 ||
      timeString.indexOf(unitMin) > -1 ||
      timeString.indexOf(unitSec) > -1
    ) {
      let tmp;

      if (timeString.indexOf(unitH) > -1) {
        hoursStr = timeString.split(unitH)[0];

        tmp = hoursStr !== null ? hoursStr.match(/\d*\.?\d+$/) : [];
        hoursStr = tmp !== null ? tmp.pop() ?? '' : '';
      }

      if (timeString.includes(unitMin)) {
        minutesStr = timeString.split(unitMin)[0];

        //remove non-numeric characters left of number (example: if string "2h 15min", then minutesStr value is currently "2h 15" and we want it to be "15")
        tmp = minutesStr != null ? minutesStr.match(/\d*\.?\d+$/) : [];
        minutesStr = tmp != null ? tmp.pop() ?? '' : '';
      }

      if (timeString.includes(unitSec)) {
        secondsStr = timeString.split(unitSec)[0];

        tmp = secondsStr !== null ? secondsStr.match(/\d*\.?\d+$/) : [];
        secondsStr = tmp !== null ? tmp.pop() ?? '' : '';
      }
    } else if (timeString.includes(TIME_SEPARATOR)) {
      //assume hours are to the left of ":" and minutes to the right (and seconds to the right of minutes if more than one ":" character)
      const fragments = timeString.split(TIME_SEPARATOR);
      hoursStr = fragments[0];
      minutesStr = fragments[1];
      secondsStr = fragments[2];
    } else {
      //assume user inserted duration in hours
      hoursStr = timeString;
    }

    //remove characters that are not numbers (or decimal separators) and convert strings to integers
    let hours = parseFloat((hoursStr + '').replace(/[^0-9.]/, '')),
      minutes = parseFloat((minutesStr + '').replace(/[^0-9.]/, '')),
      seconds = parseFloat((secondsStr + '').replace(/[^0-9.]/, ''));

    hours = isNaN(hours) ? 0 : hours;
    minutes = isNaN(minutes) ? 0 : minutes;
    seconds = isNaN(seconds) ? 0 : seconds;

    // deal decimal overflows (for example, if hours = 6.5 then we extract that 0.5 to minutes)
    // round both hours and minutes to 2 decimal places because we have float values;
    // precision more than 2 decimal places will be lost

    const hoursOverflow = Math.round((hours % 1) * 100) / 100;
    minutes += hoursOverflow * 60;
    hours -= hoursOverflow;

    const minutesOverflow = Math.round((minutes % 1) * 100) / 100;
    seconds += minutesOverflow * 60;
    minutes -= minutesOverflow;

    seconds = seconds - (seconds % 1);

    hours = Math.round(hours);
    minutes = Math.round(minutes);
    seconds = Math.round(seconds);

    //excess seconds to minutes
    if (seconds > 59) {
      minutes += Math.floor(seconds / 60);
      seconds = seconds % 60;
    }

    //excess minutes to hours
    if (minutes > 59) {
      hours += Math.floor(minutes / 60);
      minutes = minutes % 60;
    }

    return {
      hours: String(hours).padStart(2, '0'),
      minutes: String(minutes).padStart(2, '0'),
      seconds: String(seconds).padStart(2, '0'),
    };
  }

  static formatTimeFromDate(
    date: Date | string,
    adjustTimeZone: boolean = false
  ): string {
    return DateTimeService.formatTime(
      DateTimeService.getTimeFromDate(date, adjustTimeZone)
    );
  }

  static getTimeFromDate(
    date: Date | string,
    adjustTimeZone: boolean = false
  ): string {
    if (typeof date === 'string') {
      date = adjustTimeZone
        ? DateTimeService.adjustDateForTimezone(date)
        : new Date(date);
    }

    return [date.getHours(), TIME_SEPARATOR, date.getMinutes()].join('');
  }

  /**
   * @example
   * date = 2023-01-15T07:00:00"
   * machines timezone = -4
   * the JS date instance = Thu Feb 13 2021 3:00:00 GMT-0400 (Atlantic Standard Time)

   */
  static adjustDateForTimezone(date: string | number | Date): Date {
    const tmpDate = new Date(date);

    tmpDate.setMinutes(tmpDate.getMinutes() + tmpDate.getTimezoneOffset());

    return tmpDate;
  }

  /**
   * @description Correct time zone diff when converting to ISO string
   */
  static getDateISOString(
    date: Date | string,
    adjustTimeZone: boolean = true
  ): string {
    if (typeof date === 'string') {
      date = adjustTimeZone
        ? DateTimeService.adjustDateForTimezone(date)
        : new Date(date);
    }

    return isNaN(date.getTime()) ? '' : date.toISOString().split('.')[0];
  }

  static dateToNgbDateStruct(date: Date): NgbDateStruct {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    };
  }

  static formatDate(value: string | Date | number): string {
    return format(new Date(value), CONSTANTS.DATE_DISPLAY_FORMAT);
  }

  static displayDateToSqlDate(
    date: string,
    time: string | undefined = undefined
  ): string {
    let sqlFormat = CONSTANTS.DATE_SQL_FORMAT;

    const tmpDate = parse(
      date ?? '',
      CONSTANTS.DATE_DISPLAY_FORMAT,
      new Date()
    );

    if (time) {
      const { hours: startHour, minutes: startMinutes } =
        DateTimeService.parseTime(time ?? '');
      tmpDate.setHours(Number(startHour), Number(startMinutes));

      sqlFormat = CONSTANTS.DATE_TIME_SQL_FORMAT;
    }

    return format(tmpDate, sqlFormat);
  }
}
